<app-screen-shell
  contentWidth="standard"
  [contentInset]="routeMode ? 'none' : 'page'"
  [contentClass]="{'rating-review-screen': true, 'rating-review-screen-route': routeMode}"
  [showHeader]="routeMode"
  [title]="review?.event?.name || ('ratingReview.screenTitle' | translate)"
  [loading]="loading && routeMode && !(reviewReady$ | async)"
  loadingTitleKey="common.loadingData"
  [hideContentWhileLoading]="true"
  (back)="close()">

  <div class="rating-review-shell mb-4">
    @if (loading) {
      <div class="card rating-review-card">
        <div class="card-body py-3">
          <app-loading-state titleKey="common.loadingData" [compact]="true"></app-loading-state>
        </div>
      </div>
    }

    @if ((reviewReady$ | async) && completedReview) {
      <div class="card rating-review-card">
        <div class="card-body">
          <h4 class="mb-2">{{ 'ratingReview.completedTitle' | translate }}</h4>
          <p class="text-nogosh-grey3 mb-3">
            {{ 'ratingReview.completedSummary' | translate:{ rated: progress?.playersRated || 0, skipped: progress?.playersSkipped || 0 } }}
          </p>
          <button type="button" class="btn btn-primary btn-sm px-3" (click)="done()">{{ 'common.done' | translate }}</button>
        </div>
      </div>
    }

    @if ((reviewReady$ | async) && !completedReview && review && currentPlayer) {
      <div>
        <section class="card profile-content-width player-detail-content-width player-detail-card rating-review-detail-card">
          <div class="player-detail-card-body">
            <div class="player-detail-summary">
              <div class="player-detail-card-actions">
                <button type="button" class="player-detail-share-button" (click)="close()" [disabled]="saving" [attr.aria-label]="'common.close' | translate" [title]="'common.close' | translate">
                  <i [class]="'common.close' | nogoshIcon" aria-hidden="true"></i>
                </button>
              </div>
              <div class="player-detail-heading rating-review-detail-heading">
                <button type="button" class="player-detail-avatar-shell player-team-avatar player-detail-shortcut"
                  [class.is-manager-added-avatar]="currentPlayer.playerType === 'MANAGED_PLAYER' || currentPlayer.playerType === 'GUEST'">
                  <img [src]="currentPlayer.avatar ? currentPlayer.avatar : '../../assets/images/user1.jpg'"
                    onerror="this.src='../../assets/images/user1.jpg'" class="player-detail-avatar rounded-circle border"
                    [alt]="'accessibility.userProfileImage' | translate">
                  @if (currentPlayer.playerType === 'MANAGED_PLAYER' || currentPlayer.playerType === 'GUEST') {
                    <span class="player-team-status-icon" aria-hidden="true"><i [class]="'player.add' | nogoshIcon"></i></span>
                  }
                </button>
                <div class="player-detail-heading-copy">
                  <button type="button" class="player-detail-title-shortcut player-detail-shortcut" disabled>
                    <span class="player-detail-nickname mb-0 text-truncate">{{ playerDisplayName }}</span>
                  </button>
                  @if (showPlayerFullName(currentPlayer)) {
                    <p class="player-detail-name mb-0 text-truncate">{{ currentPlayer.name }}</p>
                  }
                  <div class="player-detail-context">
                    @if (joinedAt()) {
                      <span class="player-detail-meta-pill">
                        {{ 'players.joined' | translate }} {{ joinedAt() | date:'MMM yyyy' }}
                      </span>
                    }
                    <span class="player-detail-meta-pill">{{ currentIndex + 1 }} / {{ review.playersTotal }}</span>
                    @if (currentPlayer.recentlyRated) {
                      <span class="badge badge-info">{{ 'ratingReview.recentlyRated' | translate }}</span>
                    }
                    @if (currentPlayer.rated) {
                      <span class="badge badge-success">{{ 'ratingReview.rated' | translate }}</span>
                    }
                    @if (currentPlayer.skipped) {
                      <span class="badge badge-secondary">{{ 'ratingReview.skipped' | translate }}</span>
                    }
                  </div>
                </div>
              </div>
              <div class="player-detail-stats-layout rating-review-detail-stats">
                <div class="player-detail-rating-column">
                  <div class="player-detail-metric is-rating" [title]="'players.offence' | translate">
                    <span class="player-detail-metric-label">{{ 'players.offence' | translate }}</span>
                    <strong><i [class]="'player.rating' | nogoshIcon" aria-hidden="true"></i>{{ displaySliderRating(offence) || '–' }}</strong>
                  </div>
                  <div class="player-detail-metric is-rating" [title]="'players.defence' | translate">
                    <span class="player-detail-metric-label">{{ 'players.defence' | translate }}</span>
                    <strong><i [class]="'player.rating' | nogoshIcon" aria-hidden="true"></i>{{ displaySliderRating(defence) || '–' }}</strong>
                  </div>
                </div>
                <div class="player-detail-performance-column">
                  <div class="player-detail-metric player-detail-record-metric is-primary">
                    <div class="player-detail-record-value">
                      <div class="player-detail-played-count">
                        <span>{{ 'stats.played' | translate }}</span>
                        <strong>{{ profileStatValue(currentPlayer.profileStatsSummary?.playedGames) }}</strong>
                      </div>
                      <div class="player-detail-record-breakdown">
                        <span><strong>{{ profileStatValue(currentPlayer.profileStatsSummary?.playerWins) }}</strong>W</span>
                        <span><strong>{{ profileStatValue(currentPlayer.profileStatsSummary?.playerDraws) }}</strong>D</span>
                        <span><strong>{{ profileStatValue(currentPlayer.profileStatsSummary?.playerLoses) }}</strong>L</span>
                      </div>
                    </div>
                  </div>
                  <div class="player-detail-metric player-detail-goal-metric" [title]="'players.teamGoalDeltaHint' | translate">
                    <div class="player-detail-goal-value">
                      <div class="player-detail-goal-delta">
                        <span>{{ 'players.goals' | translate }} Δ</span>
                        <strong>{{ teamGoalScore() }}</strong>
                      </div>
                      <div class="player-detail-goal-breakdown">
                        <span [title]="'players.goalsForHint' | translate"><strong>{{ profileStatValue(currentPlayer.profileStatsSummary?.playerScored) }}</strong>GF</span>
                        <span [title]="'players.goalsAgainstHint' | translate"><strong>{{ profileStatValue(currentPlayer.profileStatsSummary?.playerConceded) }}</strong>GA</span>
                      </div>
                    </div>
                  </div>
                  <div class="player-detail-metric player-detail-teams-metric">
                    <span class="player-detail-metric-label">{{ 'players.teamsCount' | translate }}</span>
                    <strong>{{ profileStatValue(currentPlayer.profileStatsSummary?.teamsCount) }}</strong>
                  </div>
                </div>
              </div>
              <p class="rating-review-helper mb-0">
                {{ (currentPlayer.currentRating ? 'ratingReview.updateHint' : 'ratingReview.newHint') | translate }}
              </p>
            </div>
          </div>
        </section>

        <section class="profile-content-width player-detail-content-width player-match-rating rating-review-rating-card" [attr.aria-label]="'players.ratePlayer' | translate">
          <div class="card">
            <div class="card-body py-3 px-3">
              <div class="player-rating-panel-header">
                <span class="player-rating-panel-icon"><i [class]="'player.rating' | nogoshIcon" aria-hidden="true"></i></span>
                <b>{{ 'players.ratePlayer' | translate }} {{ playerDisplayName }}</b>
              </div>
              <div class="player-rating-row">
                <div class="player-rating-label">
                  <span>{{ 'players.offence' | translate }}</span>
                  <strong>{{ displaySliderRating(offence) || '–' }}</strong>
                </div>
                <div class="profile-rating-control player-rating-slider-control" [style.--rating-percent.%]="ratingSliderPercent(offence)">
                  <input type="range" min="0" max="10" step="0.01" [value]="offence || 0" (input)="offence = $any($event.target).valueAsNumber" (change)="setOffenceFromSlider($any($event.target).valueAsNumber)" [disabled]="saving" [attr.aria-label]="'players.offence' | translate">
                  <div class="player-rating-slider-scale" aria-hidden="true">
                    <span>0</span>
                    <span>10</span>
                  </div>
                </div>
              </div>
              <div class="player-rating-row">
                <div class="player-rating-label">
                  <span>{{ 'players.defence' | translate }}</span>
                  <strong>{{ displaySliderRating(defence) || '–' }}</strong>
                </div>
                <div class="profile-rating-control player-rating-slider-control" [style.--rating-percent.%]="ratingSliderPercent(defence)">
                  <input type="range" min="0" max="10" step="0.01" [value]="defence || 0" (input)="defence = $any($event.target).valueAsNumber" (change)="setDefenceFromSlider($any($event.target).valueAsNumber)" [disabled]="saving" [attr.aria-label]="'players.defence' | translate">
                  <div class="player-rating-slider-scale" aria-hidden="true">
                    <span>0</span>
                    <span>10</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        @if (errorMsg) {
          <p class="alert alert-danger py-2 mb-3" role="alert">{{ errorMsg }}</p>
        }
        <div class="rating-review-actions">
          <button
            type="button"
            class="btn btn-primary btn-sm px-3"
            (click)="saveCurrentPlayer()"
            [disabled]="saving || !offence || !defence">
            @if (saving) {
              <span><i [class]="('common.loading' | nogoshIcon) + ' nogosh-icon-spin'" aria-hidden="true"></i></span>
            }
            {{ 'common.next' | translate }}
          </button>
          <button type="button" class="btn btn-outline-primary btn-sm px-3" (click)="skipCurrentPlayer()" [disabled]="saving">
            {{ 'ratingReview.skip' | translate }}
          </button>
          <button type="button" class="btn btn-outline-primary btn-sm px-3" (click)="finishReview()" [disabled]="saving">
            {{ 'ratingReview.finish' | translate }}
          </button>
        </div>
      </div>
    }
  </div>
</app-screen-shell>
